import { Checkbox } from '@HoudiniUI/Checkbox';
import { DateInput } from '@HoudiniUI/DateInput';
import { Input } from '@HoudiniUI/Input';
import { Label } from '@HoudiniUI/Label';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@HoudiniUI/Select';
import { TextArea } from '@HoudiniUI/TextArea';
import { IRegEdMetadataField, IRegEdMetadataForm, RegEdFieldType } from '@Queries/RegEdCompliance/types';
import { css } from 'aphrodite';
import React from 'react';
import { useConditionalVisibility } from '../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import {
	normalizeFieldType,
	orderedActiveFields,
	regEdOptionValue,
	toFormValues,
	toSurveyForm,
} from './regEdMetadataFormAdapter';
import { styleSheet } from './styles';

export interface RegEdMetadataFormProps {
	schema: IRegEdMetadataForm | null | undefined;
	/** Answer bag keyed by field id (matches the API's `RegEdMetadataAnswers` dictionary). */
	answers: Record<string, unknown>;
	onChange: (answers: Record<string, unknown>) => void;
	disabled?: boolean;
}

const inputTypeForField = (fieldType: RegEdFieldType | undefined): string => {
	switch (fieldType) {
		case RegEdFieldType.Email:
			return 'email';
		case RegEdFieldType.Integer:
		case RegEdFieldType.Decimal:
		case RegEdFieldType.Money:
			return 'number';
		default:
			return 'text';
	}
};

/** Required markers are display-only — validation is enforced on the submit path. */
export const RegEdMetadataForm: React.FC<RegEdMetadataFormProps> = ({ schema, answers, onChange, disabled }) => {
	const fields = React.useMemo(() => orderedActiveFields(schema), [schema]);
	const surveyForm = React.useMemo(() => toSurveyForm(fields), [fields]);
	const formValues = React.useMemo(() => toFormValues(answers), [answers]);
	const visibleFieldIds = useConditionalVisibility(surveyForm, formValues);

	const setAnswer = (fieldId: string, value: unknown) => {
		onChange({ ...answers, [fieldId]: value });
	};

	const toggleMultiOption = (fieldId: string, externalId: string, checked: boolean) => {
		const current = Array.isArray(answers[fieldId]) ? (answers[fieldId] as string[]) : [];
		const next = checked ? [...current, externalId] : current.filter(v => v !== externalId);
		setAnswer(fieldId, next);
	};

	const renderWidget = (field: IRegEdMetadataField, fieldType: RegEdFieldType | undefined) => {
		const fieldId = field.id!;
		const rawValue = answers[fieldId];

		switch (fieldType) {
			case RegEdFieldType.Text:
				return (
					<TextArea
						id={fieldId}
						value={typeof rawValue === 'string' ? rawValue : ''}
						onChange={e => setAnswer(fieldId, e.target.value)}
						disabled={disabled}
						rows={4}
					/>
				);
			case RegEdFieldType.Boolean:
				return (
					<Checkbox
						checked={rawValue === true}
						onCheckedChange={checked => setAnswer(fieldId, checked === true)}
						disabled={disabled}
					/>
				);
			case RegEdFieldType.Option: {
				const current = typeof rawValue === 'string' ? rawValue : '';
				const selected = field.options?.find(o => regEdOptionValue(o) === current);
				return (
					<Select value={current} onValueChange={value => setAnswer(fieldId, value)} disabled={disabled}>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<span>{selected?.label ?? 'Select…'}</span>
						</SelectTrigger>
						<SelectContent>
							{(field.options ?? [])
								.filter(o => o.isActive !== false && !!regEdOptionValue(o))
								.map((o, i) => (
									<SelectItem key={`${regEdOptionValue(o)}-${i}`} value={regEdOptionValue(o)}>
										{o.label}
									</SelectItem>
								))}
						</SelectContent>
					</Select>
				);
			}
			case RegEdFieldType.MultipleOptions: {
				const current = Array.isArray(rawValue) ? (rawValue as string[]) : [];
				return (
					<div className={css(styleSheet.optionsGroup)}>
						{(field.options ?? [])
							.filter(o => o.isActive !== false && !!regEdOptionValue(o))
							.map((o, i) => {
								const value = regEdOptionValue(o);
								return (
									<label key={`${value}-${i}`} className={css(styleSheet.optionRow)}>
										<Checkbox
											checked={current.includes(value)}
											onCheckedChange={checked => toggleMultiOption(fieldId, value, checked === true)}
											disabled={disabled}
										/>
										<span>{o.label}</span>
									</label>
								);
							})}
					</div>
				);
			}
			case RegEdFieldType.Date:
				return (
					<DateInput
						value={typeof rawValue === 'string' ? rawValue : ''}
						onChange={value => setAnswer(fieldId, value)}
						disabled={disabled}
					/>
				);
			default:
				return (
					<Input
						id={fieldId}
						type={inputTypeForField(fieldType)}
						value={typeof rawValue === 'string' ? rawValue : ''}
						onChange={e => setAnswer(fieldId, e.target.value)}
						disabled={disabled}
					/>
				);
		}
	};

	return (
		<div className={css(styleSheet.container)}>
			{fields.map(field => {
				const fieldId = field.id!;
				if (visibleFieldIds && !visibleFieldIds.has(fieldId)) {
					return null;
				}
				const fieldType = normalizeFieldType(field.fieldType);
				// Only Input/TextArea widgets render an element with `id={fieldId}`; composite controls
				// (Select/Checkbox/MultipleOptions/DateInput) don't, so `htmlFor` would dangle.
				const hasFocusableControl =
					fieldType !== RegEdFieldType.Boolean &&
					fieldType !== RegEdFieldType.Option &&
					fieldType !== RegEdFieldType.MultipleOptions &&
					fieldType !== RegEdFieldType.Date;

				if (fieldType === RegEdFieldType.Label) {
					return (
						<div key={fieldId} className={css(styleSheet.informationalLabel)}>
							{field.label}
							{field.helpText ? ` ${field.helpText}` : ''}
						</div>
					);
				}

				return (
					<div key={fieldId} className={css(styleSheet.field)}>
						<Label styles={[styleSheet.label]} htmlFor={hasFocusableControl ? fieldId : undefined}>
							{field.label}
							{field.isRequired ? <span className={css(styleSheet.requiredMark)}>*</span> : null}
						</Label>
						{field.helpText ? <span className={css(styleSheet.helpText)}>{field.helpText}</span> : null}
						{renderWidget(field, fieldType)}
					</div>
				);
			})}
		</div>
	);
};
